import React, { useEffect, useState } from "react";
import { KeyRound, Mail, MapPin, Phone, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useData } from "../contexts/DataContext";

export default function SettingsPage() {
  const { user } = useAuth();
  const { companySettings, loadSettings, saveSettings } = useData();
  const [form, setForm] = useState({
    company_name: "CareFleet Transport Ltd",
    company_email: "info@carefleettransport.co.uk",
    contact_phone: "+44 20 7946 0123",
    address: "1 Fleet House, London Road, London, United Kingdom",
    support_url: "https://carefleettransport.co.uk",
  });

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (companySettings) {
      setForm({
        company_name: companySettings.company_name || "CareFleet Transport Ltd",
        company_email: companySettings.company_email || "info@carefleettransport.co.uk",
        contact_phone: companySettings.contact_phone || "+44 20 7946 0123",
        address: companySettings.address || "1 Fleet House, London Road, London, United Kingdom",
        support_url: companySettings.support_url || "https://carefleettransport.co.uk",
      });
    }
  }, [companySettings]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const result = await saveSettings(form);
    if (result.success) {
      toast.success("Settings saved.");
    } else {
      toast.error(result.error);
    }
  };

  const googleMapsReady = Boolean(companySettings?.google_maps_frontend_key_configured);

  return (
    <div className="space-y-8 animate-fade-in max-w-5xl">
      <div className="guardian-card bg-white p-6">
        <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Current account</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl bg-[#F8FAFC] p-4">
            <p className="text-sm text-[#64748B]">Name</p>
            <p className="mt-1 font-semibold text-[#0F172A]">{user?.name}</p>
          </div>
          <div className="rounded-2xl bg-[#F8FAFC] p-4">
            <p className="text-sm text-[#64748B]">Email</p>
            <p className="mt-1 font-semibold text-[#0F172A]">{user?.email}</p>
          </div>
          <div className="rounded-2xl bg-[#F8FAFC] p-4">
            <p className="text-sm text-[#64748B]">Role</p>
            <p className="mt-1 font-semibold capitalize text-[#0F172A]">{user?.role}</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <form onSubmit={handleSubmit} className="guardian-card bg-white p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Company profile</h2>
              <p className="mt-1 text-sm text-[#64748B]">These values power the footer, public tracking page, and branded platform shell.</p>
            </div>
            {user?.role !== "admin" && <span className="rounded-full bg-[#F8FAFC] px-3 py-1 text-xs font-semibold text-[#64748B]">Read only</span>}
          </div>
          <div className="mt-6 grid gap-4">
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Company name</label>
              <input value={form.company_name} disabled={user?.role !== "admin"} onChange={(event) => setForm((current) => ({ ...current, company_name: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm disabled:bg-[#F8FAFC]" />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-[#0F172A]">Email</label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={16} />
                  <input value={form.company_email} disabled={user?.role !== "admin"} onChange={(event) => setForm((current) => ({ ...current, company_email: event.target.value }))} className="w-full rounded-xl border border-[#E2E8F0] px-10 py-3 text-sm disabled:bg-[#F8FAFC]" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#0F172A]">Phone</label>
                <div className="relative mt-2">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={16} />
                  <input value={form.contact_phone} disabled={user?.role !== "admin"} onChange={(event) => setForm((current) => ({ ...current, contact_phone: event.target.value }))} className="w-full rounded-xl border border-[#E2E8F0] px-10 py-3 text-sm disabled:bg-[#F8FAFC]" />
                </div>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Address</label>
              <div className="relative mt-2">
                <MapPin className="absolute left-3 top-4 text-[#94A3B8]" size={16} />
                <textarea value={form.address} disabled={user?.role !== "admin"} onChange={(event) => setForm((current) => ({ ...current, address: event.target.value }))} className="min-h-[110px] w-full rounded-xl border border-[#E2E8F0] px-10 py-3 text-sm disabled:bg-[#F8FAFC]" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Support URL</label>
              <input value={form.support_url} disabled={user?.role !== "admin"} onChange={(event) => setForm((current) => ({ ...current, support_url: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm disabled:bg-[#F8FAFC]" />
            </div>
          </div>
          {user?.role === "admin" && (
            <button type="submit" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#163D6B] px-4 py-3 text-sm font-semibold text-white">
              <Save size={16} /> Save settings
            </button>
          )}
        </form>

        <div className="space-y-6">
          <div className="guardian-card bg-white p-6">
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-[#F8FAFC] p-3 text-[#163D6B]"><KeyRound size={20} /></div>
              <div>
                <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Google Maps readiness</h2>
                <p className="mt-1 text-sm text-[#64748B]">Map views are fully wired. Add the environment variable and restart the frontend to activate interactive maps.</p>
              </div>
            </div>
            <div className="mt-5 rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-sm text-[#475569]">
              <p className="font-semibold text-[#0F172A]">Frontend key status: {googleMapsReady ? "Configured" : "Missing"}</p>
              <p className="mt-2">Expected variable: <code>REACT_APP_GOOGLE_MAPS_API_KEY</code></p>
              <p className="mt-2">Optional backend ETA key: <code>GOOGLE_MAPS_API_KEY</code></p>
            </div>
          </div>

          <div className="guardian-card bg-white p-6">
            <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Implementation notes</h2>
            <ul className="mt-4 space-y-3 text-sm text-[#475569]">
              <li>• Contact details are placeholder values and can be edited here later.</li>
              <li>• Password reset is admin-led and token-based in this phase.</li>
              <li>• GPS tracking starts only while a trip is in progress and stops on completion.</li>
              <li>• Parent tracking uses secure token links instead of public trip identifiers.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
